import { useEffect, useRef, useState, useCallback } from 'react';
import { generateClient } from '@aws-amplify/api';
import { SUBSCRIBE_EMOJI_REACTIONS } from '../graphql/operations';

const client = generateClient();

/**
 * Custom hook to manage emoji reactions subscription with proper state management
 * Avoids stale data issues by managing subscription lifecycle directly
 */
const useEmojiReactionsSubscription = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const subscriptionRef = useRef(null);
  const listenerCallbackRef = useRef(null);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Subscribe to emoji reactions
  const subscribe = useCallback((onEmojiReceived) => {
    listenerCallbackRef.current = onEmojiReceived;

    if (!isOnline) {
      return () => {}; // Return empty cleanup function
    }

    const setupSubscription = () => {
      try {
        setSubscriptionError(null);
        
        const subscription = client.graphql({
          query: SUBSCRIBE_EMOJI_REACTIONS
        }).subscribe({
          next: ({ data }) => {
            if (data?.onEmojiCounts?.counts?.[0]?.emoji && listenerCallbackRef.current) {
              const emojiEvent = data.onEmojiCounts.counts[0];
              listenerCallbackRef.current(emojiEvent);
            }
          },
          error: (error) => {
            console.error('Emoji subscription error:', error);
            setSubscriptionError(error);
          }
        });

        subscriptionRef.current = subscription;
      } catch (error) {
        console.error('Failed to setup emoji subscription:', error);
        setSubscriptionError(error);
      }
    };

    setupSubscription();

    // Return cleanup function
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      listenerCallbackRef.current = null;
    };
  }, [isOnline]);

  // Cleanup subscription when going offline
  useEffect(() => {
    if (!isOnline && subscriptionRef.current) {
      subscriptionRef.current.unsubscribe();
      subscriptionRef.current = null;
    }
  }, [isOnline]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, []);

  return {
    subscribe,
    isOnline,
    subscriptionError
  };
};

export default useEmojiReactionsSubscription;
